import { NgClass, NgComponentOutlet, NgIf, NgSwitch, NgSwitchCase, NgSwitchDefault, NgTemplateOutlet } from '@angular/common';
import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnInit, Output, inject } from '@angular/core';
import { Filter } from '@df/catalog-ui/listing/filter/filter';
import { FilterAttributeRange } from '@df/catalog-ui/listing/filter/filter-attribute-range';
import { IProductDataV2 } from '@df/catalog/product/product';
import { App } from '@df/core/app';
import { AdvancedCommerceListingFiltersComponentDef, AdvancedCommerceStore } from '@df/module-advanced-commerce';
import { DfUiModule } from '@df/ui/df-ui.module';
import { DfModalService } from '@df/ui/modal/df-modal.service';
import { PriceComponent } from '@df/ui/price/price.component';
import { TranslationModule } from '@df/ui/translation/translation.module';
import flatten from 'lodash-es/flatten';
import { LocalProduct } from '../../../catalog/model/product/product.decorator';
import { ICmsOutletBlockComponent } from '../../../catalog/outlet-block/icms-outlet-block.component';
import { toggleTieredPriceModeListingEvent } from '../../../catalog/ui/listing/product-listing-tools.component';
import { NListing } from '../../../catalog/ui/listing/product-listing.interface';
import { IfTradeStoreDirective } from '../../../multisite/trade/if-trade-store.directive';
import { StickyFreeComponent } from '../../../ui/sticky-free/sticky-free.component';
import { VipService } from '../../vip/vip.service';
import { NAdvancedCommerceListingFiltersModal } from './filter/advanced-commerce-filter-modal.component';
import { AdvancedCommerceListingSizeComponent } from './size/advanced-commerce-listing-size.component';
import { AdvancedCommerceProductListingSortComponent } from './sort/advanced-commerce-advanced-commerce-product-listing-sort.component';

export interface IAcFilterSelectedOption {
    filterType: 'ac-filter';
    label: string;
    value: string | number;
    matchesCount: number;
    unselect: (filter: AdvancedCommerceStore.Filters.Filter, option: AdvancedCommerceStore.Filters.Option) => void;
    filterProp: AdvancedCommerceStore.Filters.Filter;
    optionProp: AdvancedCommerceStore.Filters.Option;
}

export interface IAcFilterSelectedRange {
    filterType: 'ac-filter-range';
    label: string;
    from: number;
    to: number;
    unselect: () => void;
    filterProp: AdvancedCommerceStore.Filters.Filter;
}

export type IAcFilterSelected = IAcFilterSelectedOption | IAcFilterSelectedRange;

@Component({
    selector: 'ac-product-listing-tools',
    templateUrl: '../../../catalog/ui/listing/product-listing-tools.html',
    styleUrls: ['../../../catalog/ui/listing/product-listing.scss'],
    standalone: true,
    imports: [
        DfUiModule,
        ICmsOutletBlockComponent,
        IfTradeStoreDirective,
        NgComponentOutlet,
        NgIf,
        NgSwitch,
        NgSwitchCase,
        NgSwitchDefault,
        NgTemplateOutlet,
        NgClass,
        PriceComponent,
        TranslationModule,
        StickyFreeComponent
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class AdvancedCommerceListingToolsComponent
    extends AdvancedCommerceListingFiltersComponentDef
    implements OnInit, NListing.ILisitngTotals
{
    @Input()
    type: 'bulkOrder' | 'plp' = 'plp';
    @Output()
    tieredPriceModeChange = new EventEmitter();

    private _tieredPrice = true;

    readonly DfModalService: DfModalService | undefined = inject(DfModalService);
    readonly app = inject(App);
    private vipService = inject(VipService);
    ngOnInit() {
        this.toggleTieredPriceMode(true);
        //   this.timeoutService?.setTimeout(() => this.onListingUpdate(), 0);
    }
    showFiltersModal(): void {
        this.DfModalService?.open(undefined, NAdvancedCommerceListingFiltersModal.id, undefined);
    }
    get noResults(): boolean {
        return this.store.total() === 0;
    }
    get tieredPrice(): boolean {
        return !!this._tieredPrice;
    }
    set tieredPrice(value: boolean) {
        this._tieredPrice = value;
        this.toggleTieredPriceMode(value);
        this.changeDetectorRef.detectChanges();
    }
    private toggleTieredPriceMode(value: boolean): void {
        this.app.events.broadcast(toggleTieredPriceModeListingEvent, value);
        this.tieredPriceModeChange.emit(value);
    }
    get getTotal(): number {
        return this.vipService.adjustCountForRestrictions(this.store.metaCount());
    }
    get sortOutlet(): NListing.IOutlet {
        return { component: AdvancedCommerceProductListingSortComponent, inputs: { sort: true } };
    }
    get sizeOutlet(): NListing.IOutlet {
        return { component: AdvancedCommerceListingSizeComponent, inputs: { size: true } };
    }
    get bulkOrderSortOutlet(): NListing.IOutlet {
        return { component: AdvancedCommerceProductListingSortComponent, inputs: { sort: true } };
    }
    get filtersWithSelection(): IAcFilterSelected[] | undefined {
        const optionFilters = flatten(
            this.store.filtersWithSelection().map(filter =>
                filter.options
                    ?.filter(option => option.selected)
                    .map(
                        option =>
                            ({
                                filterType: 'ac-filter',
                                label: filter.label,
                                value: option.label,
                                matchesCount: option.count || 0,
                                unselect: (filter: AdvancedCommerceStore.Filters.Filter, option: AdvancedCommerceStore.Filters.Option) => {
                                    this.store.filtersOptionUnselect(filter, option);
                                },
                                filterProp: filter,
                                optionProp: option
                            }) as IAcFilterSelectedOption
                    )
            )
        ).filter(Boolean) as IAcFilterSelectedOption[];

        const rangeFilters: IAcFilterSelectedRange[] = [];
        const priceFilter = this.store.filtersWithOptions().find(f => f.id === 'price');
        if (priceFilter && (this.store.filtersPriceFrom() || this.store.filtersPriceTo())) {
            const currentFrom = this.store.filtersPriceFrom() || priceFilter.meta?.min || 0;
            const currentTo = this.store.filtersPriceTo() || priceFilter.meta?.max || 0;
            const defaultMin = priceFilter.meta?.min || 0;
            const defaultMax = priceFilter.meta?.max || 0;

            if (currentFrom !== defaultMin || currentTo !== defaultMax) {
                rangeFilters.push({
                    filterType: 'ac-filter-range',
                    label: priceFilter.label,
                    from: currentFrom,
                    to: currentTo,
                    unselect: () => {
                        this.store.filtersPriceFromSet(priceFilter, defaultMin);
                        this.store.filtersPriceToSet(priceFilter, defaultMax);
                    },
                    filterProp: priceFilter
                });
            }
        }

        return [...optionFilters, ...rangeFilters];
    }
    protected clearAllFilters(): void {
        this.store.filtersResetSelection();
    }
    protected asRangeFilter(filter): FilterAttributeRange<LocalProduct<IProductDataV2>> {
        return filter as any as FilterAttributeRange<LocalProduct<IProductDataV2>>;
    }
    protected asAcFilter(filter): IAcFilterSelectedOption {
        return filter as any as IAcFilterSelectedOption;
    }
    protected asAcFilterRange(filter): IAcFilterSelectedRange {
        return filter as any as IAcFilterSelectedRange;
    }
}
